<template>
  <v-card color="#1e1e1e" class="mov-dialog">
    <div class="cabecera">
      <span class="icono" :class="esIngreso ? 'verde' : 'rojo'">
        <v-icon :color="esIngreso ? '#66bb6a' : '#ff5252'">{{ esIngreso ? "mdi-cash-plus" : "mdi-cash-minus" }}</v-icon>
      </span>
      <div class="titulos">
        <div class="titulo">{{ esIngreso ? "Ingreso de dinero" : "Retiro de dinero" }}</div>
        <div class="sub">{{ esIngreso ? "Efectivo que entra a la caja" : "Efectivo que sale de la caja" }}</div>
      </div>
      <v-btn icon @click="$emit('close-dialog')"><v-icon>mdi-close</v-icon></v-btn>
    </div>

    <v-card-text class="pt-2">
      <v-text-field
        v-model="descripcion"
        :label="esIngreso ? 'Motivo (opcional)' : 'Motivo del retiro (opcional)'"
        outlined
        dense
        autofocus
        maxlength="200"
        hide-details
        class="mb-4"
      ></v-text-field>

      <div class="modo">
        <button type="button" :class="{ activo: modo === 'monto' }" @click="modo = 'monto'">Escribir el monto</button>
        <button type="button" :class="{ activo: modo === 'billetes' }" @click="modo = 'billetes'">Contar billetes</button>
      </div>

      <v-text-field
        v-if="modo === 'monto'"
        v-model.number="montoDirecto"
        type="number"
        min="0"
        inputmode="numeric"
        prefix="$"
        outlined
        autocomplete="off"
        hide-details
        class="monto-input"
        @keyup.enter="registrar"
      ></v-text-field>
      <contador-billetes v-else @input="setBilletes" />
    </v-card-text>

    <div class="pie">
      <div class="total">
        <div class="total-label">{{ esIngreso ? "Ingresa" : "Sale" }}</div>
        <div class="total-valor" :class="esIngreso ? 'verde' : 'rojo'">{{ monto | pesos }}</div>
      </div>
      <v-btn
        depressed
        x-large
        color="#BA6B30"
        class="white--text btn-registrar"
        :disabled="monto < 1 || loading"
        :loading="loading"
        @click="registrar"
      >
        {{ esIngreso ? "Registrar ingreso" : "Registrar retiro" }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import ContadorBilletes from "@/components/caja/ContadorBilletes.vue";

// Ingreso o retiro manual de efectivo sobre el arqueo vigente. Un solo
// componente para los dos porque el formulario y el POST son idénticos.
export default {
  name: "MovimientoCajaDialog",
  components: { ContadorBilletes },
  filters: {
    pesos(value) {
      return `$ ${new Intl.NumberFormat("es-AR").format(Math.round(Number(value) || 0))}`;
    },
  },
  props: {
    tipo: { type: String, required: true, validator: (t) => ["INGRESO", "RETIRO"].includes(t) },
  },
  data() {
    return {
      descripcion: "",
      modo: "monto",
      montoDirecto: null,
      montoBilletes: 0,
      loading: false,
    };
  },
  computed: {
    esIngreso() {
      return this.tipo === "INGRESO";
    },
    monto() {
      return this.modo === "monto" ? Number(this.montoDirecto) || 0 : this.montoBilletes;
    },
  },
  methods: {
    setBilletes(billetes) {
      this.montoBilletes = billetes.reduce(
        (acc, b) => acc + (Number(b.cantidad) || 0) * Number(b.valorBillete),
        0
      );
    },
    async registrar() {
      if (this.monto < 1 || this.loading) return;
      this.loading = true;
      const payload = { monto: this.monto };
      if (this.descripcion) payload.descripcion = this.descripcion;
      try {
        const url = this.esIngreso ? "/sucursales/arqueo/ingreso" : "/sucursales/arqueo/retiro";
        const { data } = await this.axios.post(url, payload);
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          button: false,
          color: "success",
          text: this.esIngreso ? "Ingreso registrado" : "Retiro registrado",
          icon: "mdi-check",
          timeout: 3000,
        });
        this.$emit("process-dinero", this.esIngreso ? data.monto_ingresado : data.monto_retirado);
      } catch (e) {
        const r = e.response && e.response.data;
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          button: false,
          color: "error",
          text: (r && (r.detail || r.data)) || "No se pudo registrar el movimiento.",
          icon: "mdi-alert",
          timeout: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.mov-dialog {
  border-radius: 18px !important;
}
.cabecera {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 12px 10px 20px;
}
.icono {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.icono.verde {
  background: rgba(102, 187, 106, 0.12);
}
.icono.rojo {
  background: rgba(255, 82, 82, 0.12);
}
.titulos {
  flex: 1;
}
.titulo {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1.1;
}
.sub {
  font-size: 13px;
  color: #8a8a8a;
}
.modo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  background: #121212;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 14px;
}
.modo button {
  border: none;
  background: transparent;
  color: #8a8a8a;
  font: inherit;
  font-weight: 600;
  padding: 8px;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.modo button.activo {
  background: rgba(186, 107, 48, 0.25);
  color: #fff;
}
.monto-input >>> input {
  font-size: 32px;
  font-weight: 700;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.monto-input >>> .v-text-field__prefix {
  font-size: 24px;
  color: #8a8a8a;
}
.pie {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}
.total-label {
  font-size: 12px;
  color: #8a8a8a;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.total-valor {
  font-size: 28px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.verde {
  color: #66bb6a;
}
.rojo {
  color: #ff5252;
}
.btn-registrar {
  border-radius: 12px;
  font-weight: 700;
}
</style>
